import { ThunkAction } from '../../store/middleware';

import { IImage } from '../../api/images';
import { currentUserId } from '../../entities/auth/session/selectors';
import { idOrLegacyId } from '../../entities/properties/selectors';
import { logging } from '../../logging';
import { ActionTypeNames, ActionValues, createActionCreators } from '../../store/actions';
import { diffImageSet, getDimensionsFromFileData, upload } from './helpers';
import { imagesForProperty } from './selectors';

export const getImagesActions = createActionCreators<
	{ T: 'IMAGES_GET_BY_PROPERTY_ID_REQUEST', P: { id: string } },
	{ T: 'IMAGES_GET_BY_PROPERTY_ID_RECEIVE', P: { id: string, images: IImage[] } },
	{ T: 'IMAGES_GET_BY_PROPERTY_ID_FAILURE', P: { id: string, error: Error } }
>('IMAGES_GET_BY_PROPERTY_ID_REQUEST', 'IMAGES_GET_BY_PROPERTY_ID_RECEIVE', 'IMAGES_GET_BY_PROPERTY_ID_FAILURE');

export const getImagesByPropertyId = (id: number): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	dispatch(getImagesActions.IMAGES_GET_BY_PROPERTY_ID_REQUEST({ id: propertyId }));
	try {
		const images = await api.images.getByPropertyId(propertyId);
		dispatch(getImagesActions.IMAGES_GET_BY_PROPERTY_ID_RECEIVE({ id: propertyId, images }));
	} catch (error) {
		dispatch(getImagesActions.IMAGES_GET_BY_PROPERTY_ID_FAILURE({ id: propertyId, error }));
		throw error;
	}
};

// NOTE: Task-Based Property relies on these action names to function correctly, as it using the Portal
// PropertyEditPhotos (and these images actions) to provide its photos task.
// Please check this is safe to modify there as well.
export const deleteImagesActions = createActionCreators<
	{ T: 'IMAGES_DELETE_FROM_PROPERTY_REQUEST', P: { id: string, images: IImage[] } },
	{ T: 'IMAGES_DELETE_FROM_PROPERTY_RECEIVE', P: { id: string, images: IImage[], result: IImage[], current: IImage[] } },
	{ T: 'IMAGES_DELETE_FROM_PROPERTY_FAILURE', P: { id: string, images: IImage[], error: Error } }
>('IMAGES_DELETE_FROM_PROPERTY_REQUEST', 'IMAGES_DELETE_FROM_PROPERTY_RECEIVE', 'IMAGES_DELETE_FROM_PROPERTY_FAILURE');

export const deleteImagesFromProperty = (id: number, images: IImage[]): ThunkAction =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		dispatch(deleteImagesActions.IMAGES_DELETE_FROM_PROPERTY_REQUEST({ id: propertyId, images }));
		try {
			const result = await api.images.deleteImages(propertyId, images);
			const current = imagesForProperty(propertyId, getState());
			dispatch(deleteImagesActions.IMAGES_DELETE_FROM_PROPERTY_RECEIVE({ id: propertyId, images, result, current }));
		} catch (error) {
			dispatch(deleteImagesActions.IMAGES_DELETE_FROM_PROPERTY_FAILURE({ id: propertyId, images, error }));
			throw error;
		}
	};

export const setImageOrderActions = createActionCreators<
	{ T: 'IMAGES_SET_ORDER_FOR_PROPERTY_REQUEST', P: { id: string, images: IImage[] } },
	{ T: 'IMAGES_SET_ORDER_FOR_PROPERTY_RECEIVE', P: { id: string, images: IImage[], ownerId: string } },
	{ T: 'IMAGES_SET_ORDER_FOR_PROPERTY_FAILURE', P: { id: string, images: IImage[], error: Error } }
>('IMAGES_SET_ORDER_FOR_PROPERTY_REQUEST', 'IMAGES_SET_ORDER_FOR_PROPERTY_RECEIVE', 'IMAGES_SET_ORDER_FOR_PROPERTY_FAILURE');

export const setImageOrderForProperty = (id: number, updated: IImage[]): ThunkAction =>
	async (dispatch, getState, { _deprecatedApi: api }) => {
		const propertyId = idOrLegacyId(id);
		const imagesToUpdate = diffImageSet(updated);
		dispatch(setImageOrderActions.IMAGES_SET_ORDER_FOR_PROPERTY_REQUEST({ id: propertyId, images: imagesToUpdate }));
		try {
			const result = await api.images.setImageOrder(propertyId, imagesToUpdate);
			const ownerId = currentUserId(getState());
			dispatch(setImageOrderActions.IMAGES_SET_ORDER_FOR_PROPERTY_RECEIVE({ id: propertyId, images: result, ownerId }));
		} catch (error) {
			dispatch(setImageOrderActions.IMAGES_SET_ORDER_FOR_PROPERTY_FAILURE({ id: propertyId, images: imagesToUpdate, error }));
			throw error;
		}
	};

// NOTE: Task-Based Property relies on these action names to function correctly, as it using the Portal
// PropertyEditPhotos (and these images actions)  to provide its photos task.
// Please check this is safe to modify there as well.
export const uploadImagesActions = createActionCreators<
	{ T: 'IMAGES_UPLOAD_REQUEST', P: { id: string } },
	{ T: 'IMAGES_UPLOAD_RECEIVE', P: { id: string, uploaded: IImage[], current: IImage[] } },
	{ T: 'IMAGES_UPLOAD_SENT', P: { id: string } },
	{ T: 'IMAGES_UPLOAD_FAILURE', P: { id: string, error: { error: string, status: number } } },
	{ T: 'IMAGES_UPLOAD_PERCENT', P: { id: string, percent: number } }
>('IMAGES_UPLOAD_REQUEST', 'IMAGES_UPLOAD_RECEIVE', 'IMAGES_UPLOAD_SENT', 'IMAGES_UPLOAD_FAILURE', 'IMAGES_UPLOAD_PERCENT');

export const uploadImagesToProperty = (id: number, files: FileList): ThunkAction => async (dispatch, getState, { _deprecatedApi: api }) => {
	const propertyId = idOrLegacyId(id);
	const currentImgSet = getState().entities.images.byPropertyId[propertyId];
	dispatch(uploadImagesActions.IMAGES_UPLOAD_REQUEST({ id: propertyId }));
	try {
		const toUpload = Array.from(files);
		// get initial image dimensions
		const payload = await Promise.all(toUpload.map(async (file) => {
			const { width, height } = await getDimensionsFromFileData(file);
			return { width, height, name: file.name };
		}));
		// create DB records and get direct-to-s3 config
		const presignedPosts = await api.images.registerForUpload(propertyId, payload);
		if ('error' in presignedPosts) throw { response: { data: { error: 'Error registering images for upload' } } };
		// upload image data straight to s3
		const results = await Promise.all(upload(api, presignedPosts, toUpload, (percent) => {
			dispatch(uploadImagesActions.IMAGES_UPLOAD_PERCENT({ id: propertyId, percent }));
		}));
		dispatch(uploadImagesActions.IMAGES_UPLOAD_SENT({ id: propertyId }));
		// filter errors and set successful uploads to be active
		const successes = results.filter((r) => r.result.status === 204);
		if (!successes || !successes.length) throw { response: { data: { error: 'Error uploading images' } } };
		const finalised = await api.images.update(propertyId, successes.map((success, i) => {
			return { id: success.id, property_id: propertyId, is_active: true, sort_order: (currentImgSet || []).length + i };
		}));
		// trigger async resizing job
		await api.images.resizePropertyImages(propertyId).catch((e) => {
			logging.error('Failed to trigger image resize', e);
		});
		// success
		const current = imagesForProperty(propertyId, getState());
		dispatch(uploadImagesActions.IMAGES_UPLOAD_RECEIVE({ id: propertyId, uploaded: finalised, current }));
	} catch (error) {
		logging.error('Image upload error. ', error);
		const { response = {} } = error;
		const message = response && response.data ? response.data.error : (error.message || 'Unknown error');
		const status = response ? response.status : -1;
		dispatch(uploadImagesActions.IMAGES_UPLOAD_FAILURE({ id: propertyId, error: { error: message, status } }));
		throw error;
	}
};

const actions = { ...getImagesActions, ...deleteImagesActions, ...uploadImagesActions, ...setImageOrderActions };

type ImagesActions = ActionValues<typeof actions>;
type ImagesActionTypes = ActionTypeNames<ImagesActions>;
export type Actions = ImagesActions[keyof ImagesActions];
export const ActionTypes = Object.keys(actions).reduce((types, t) => {
	types[t] = t;
	return types;
}, {} as ImagesActionTypes);
